/**
 * @file        src/modals/GroupQuoteModal.jsx
 * @description "Flight Request" group-booking quote form. Ported from
 *              `#groupQuoteModal` (~line 2023) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-25 Claude — The success screen no longer depends on the EMAIL.
 *                         Every request is now recorded in the admin console's
 *                         Group Booking Requests queue before it is mailed
 *                         (admin migration 073), so a mail failure alone no
 *                         longer loses the lead — and refusing the success
 *                         screen for one would only make the customer submit a
 *                         request we already hold. A total loss (neither
 *                         recorded nor emailed) is still reported, and an
 *                         absent `recorded` counts as a loss so an older
 *                         backend behaves exactly as before.
 *   - 2026-09-25 Claude — Mobile pre-fills from the signed-in customer's profile,
 *                          like the email already did. The modal stays mounted
 *                          while closed, so an effect fills the field when the
 *                          /me/profile read resolves — only while it is empty.
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-08-11 Claude — Accepts the SELECTED itinerary via the modal `payload`
 *                         ({tripType, legs[], tripPriced}) and quotes THAT, instead of
 *                         only the searched route. GET QUOTE is now reachable from a
 *                         round trip and a multi-city, so every leg is mapped into the
 *                         template's `flights[]` — both legs of a return, all N of a
 *                         multi-city — carrying airline / flight number / route / date /
 *                         fare, plus times, duration, stops and cabin as additive keys
 *                         the template ignores today. `price` is PER PERSON to match the
 *                         template's own "(per person)" total row. No backend change:
 *                         the endpoint, payload keys and email are the existing ones,
 *                         and with no payload the request falls back to the old
 *                         route-only shape byte for byte.
 *   - 2026-08-11 Claude — Payload mapped to the EXISTING emailTemplates.FlightQuote
 *                         contract (no backend change): `flightDetails` is the object
 *                         shape the template reads — `{from,to,date}` for one way,
 *                         `{tripType,flights[]}` for return / multi-city — and `paxInfo`
 *                         is `{adults,children,infants}` as NUMBERS. Sending the
 *                         formatted strings I first used rendered the Flight Details
 *                         table as a row of "—", dropped the Passengers row and left the
 *                         subject route blank. The success screen now also requires
 *                         `emailSent !== false`: the endpoint answers 200 even when the
 *                         mail fails and persists nothing, so a 2xx alone can mean the
 *                         request reached no one.
 *   - 2026-08-11 Claude — SUBMIT REQUEST now validates and actually SENDS. It used to be
 *                         `onClick={() => setDone(true)}`: an empty form passed straight
 *                         to "Request submitted", and no form — full or empty — was ever
 *                         posted anywhere, so every group enquiry was discarded while the
 *                         customer was told a specialist would come back within 12–24
 *                         hours. Name / phone / email are now required inline, and the
 *                         request goes to POST /quote-request (which mails the brand's
 *                         support inbox). The success screen is shown only on a 2xx.
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-06-22 Claude — Initial port. Local state for form + success view
 *                         (replaces the source's `#gqForm` / `#gqSuccess`
 *                         show/hide pair).
 */
import { useEffect, useState } from 'react';
import PhoneInput, { phoneError } from '../components/PhoneInput.jsx';
import SelectMenu from '../components/SelectMenu.jsx';
import { useBooking } from '../store/booking.store.js';
import { requestFlightQuote } from '../api/quotes.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The searched route, one entry per leg, in travel order. Used on its own when
 * no flight has been chosen, and as the per-leg FALLBACK when one has — a
 * supplier routinely answers a DXB leg with a nearby airport, so a card's own
 * from/to is the truth and this is only the backstop.
 */
function searchedRoute(fs, trip) {
  if (trip === 'multicity') {
    return (Array.isArray(fs.legs) ? fs.legs : [])
      .filter((l) => l && l.from && l.to)
      .map((l) => ({ from: l.from, to: l.to, date: l.date || '' }));
  }
  if (trip === 'return' && fs.returnDate) {
    return [
      { from: fs.from, to: fs.to, date: fs.departDate || '' },
      { from: fs.to, to: fs.from, date: fs.returnDate || '' },
    ];
  }
  return [{ from: fs.from, to: fs.to, date: fs.departDate || '' }];
}

/**
 * One CHOSEN flight → one `flights[]` entry. `airline` / `flightNo` / `price` /
 * `fareType` are the four columns the email's Flight Details table prints beside
 * the route and date; the keys after them are additive context (a group is
 * quoted off times, stops and cabin) that the template ignores today, so nothing
 * already rendered changes.
 *
 * `price` is deliberately PER PERSON: the template's own summary row is labelled
 * "Estimated Total (per person)", so sending the party total would misprice the
 * quote by the passenger count. `paxInfo` carries the headcount separately.
 * `perLegPrice` is false for an international COMBO, where one fare covers the
 * whole journey and every leg card repeats it — printing it per row would quote
 * the trip N times.
 */
function legFromCard(card, fallback, concession, perLegPrice) {
  const rt = fallback || {};
  const per = Math.round(Number(card.price) || 0);
  return {
    airline: card.airline || undefined,
    flightNo: card.code || undefined,
    from: card.fromCode || card.from || rt.from || '',
    to: card.toCode || card.to || rt.to || '',
    date: String(card.depISO || '').slice(0, 10) || rt.date || '',
    price: perLegPrice && per ? per : undefined,
    fareType: [card.fareName, concession].filter(Boolean).join(' · ') || undefined,
    depTime: card.depISO || undefined,
    arrTime: card.arrISO || undefined,
    duration: card.dur || undefined,
    stops: card.stops || undefined,
    cabin: card.cabinName || undefined,
  };
}

/**
 * Build the `flightDetails` OBJECT that emailTemplates.FlightQuote expects. The
 * template is not free-text: it reads `from` / `to` / `date` off the object for a
 * single leg, off `flights[]` for a multi-leg trip, and the SUBJECT LINE is built
 * from those same keys ("New Flight Quote Request - BOM → DEL"). Passing a
 * pre-formatted string renders the whole Flight Details table as "—" and leaves
 * the subject route blank, so the shape below is load-bearing.
 *
 * `selected` is the itinerary the customer pressed GET QUOTE on (results page,
 * Group booking mode): `{ tripType, legs[], tripPriced }`, legs being adapted
 * flight cards in travel order — ALL of them, so a round trip quotes both legs
 * and a multi-city quotes every one. Without it (the modal opened with no flight
 * chosen) the request falls back to the searched ROUTE alone, exactly as before:
 * airline / flightNo / price are then absent and the template prints "—" rather
 * than inventing a flight nobody picked.
 */
function buildFlightDetails(state, selected) {
  const fs = state.flightSearch || {};
  // What the results behind this modal actually ARE, not what the radio has been
  // flipped to since — searchedTripType is stamped at Search time.
  const trip = selected?.tripType || fs.searchedTripType || state.tripType || 'oneway';
  const fareType = fs.fareType || 'REGULAR';

  const cards = (Array.isArray(selected?.legs) ? selected.legs : []).filter(Boolean);
  if (cards.length) {
    const route = searchedRoute(fs, trip);
    // A concession is a property of the SEARCH, not of the fare brand on the
    // card, so it is appended rather than replacing it: "Published · STUDENT".
    const concession = fareType && fareType !== 'REGULAR' ? fareType.replace(/_/g, ' ') : '';
    const perLegPrice = !selected.tripPriced;
    const flights = cards.map((c, i) => legFromCard(c, route[i], concession, perLegPrice));
    const totalPrice = perLegPrice
      ? flights.reduce((sum, l) => sum + (l.price || 0), 0)
      : Math.round(Number(cards[0].price) || 0);
    if (flights.length > 1) {
      return {
        tripType: trip,
        // Kept at the top level too — the multi-leg subject reads firstLeg.from,
        // but these make the object self-describing for any other reader.
        from: flights[0].from,
        to: flights[flights.length - 1].to,
        fareType,
        flights,
        totalPrice: totalPrice || undefined,
      };
    }
    // No `totalPrice` on a single leg: the template would print the same figure
    // twice, once in the table's Price column and again as "Estimated Total".
    return { ...flights[0], tripType: 'oneway', fareType: flights[0].fareType || fareType };
  }

  const leg = (from, to, date) => ({ from, to, date: date || '', fareType });

  if (trip === 'multicity') {
    const flights = (Array.isArray(fs.legs) ? fs.legs : [])
      .filter((l) => l && l.from && l.to)
      .map((l) => leg(l.from, l.to, l.date));
    // Only claim multi-city when there are legs to show. With an empty array the
    // template falls back to [flightDetails] and prints a row of "—".
    if (flights.length) return { tripType: 'multicity', flights, fareType };
  }
  if (trip === 'return' && fs.returnDate) {
    return {
      tripType: 'return',
      // Kept at the top level too — the multi-leg subject reads firstLeg.from, but
      // these make the object self-describing for any other reader.
      from: fs.from,
      to: fs.to,
      fareType,
      flights: [leg(fs.from, fs.to, fs.departDate), leg(fs.to, fs.from, fs.returnDate)],
    };
  }
  return { ...leg(fs.from, fs.to, fs.departDate), tripType: 'oneway' };
}

export default function GroupQuoteModal({ open, onClose, payload }) {
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  // Email and mobile default to the signed-in account's own profile (no
  // hardcoded address, and no asking a customer for a number we already hold).
  const userPhone = useBooking((s) => s.userPhone);
  const [form, setForm] = useState({
    name: '',
    phone: useBooking.getState().userPhone || '',
    email: useBooking.getState().userEmail || '',
    adults: '1',
    children: '0',
    infants: '0',
    budget: '',
    remarks: '',
  });

  // `userPhone` is read from /me/profile, so it can land after this modal was
  // first mounted (it stays mounted and returns null while closed). Fill only
  // while the field is still empty — never over what the customer typed.
  useEffect(() => {
    if (userPhone) setForm((f) => (f.phone ? f : { ...f, phone: userPhone }));
  }, [userPhone]);

  useEffect(() => {
    if (!open) {
      setDone(false);
      // Clear the previous attempt's errors too, so reopening the modal does not
      // greet the customer with red text about a form they have not filled yet.
      setErrors({});
      setSendError('');
      setSending(false);
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Typing in a field clears that field's error — the message goes when the
  // problem does, rather than sitting there until the next submit.
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((p) => (p[k] ? { ...p, [k]: '' } : p));
  };
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // The three starred fields are the ones the SERVER also requires (name, mobile,
  // email) — validating the same set here means a valid form can never be refused
  // by the backend, and an invalid one never costs a round trip.
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    // Strip the dial code before counting: PhoneInput emits "+91 98765 43210", so
    // the raw string is never empty even when no number has been typed.
    const phoneDigits = String(form.phone || '').replace(/^\+\d+/, '').replace(/\D/g, '');
    if (!phoneDigits) e.phone = 'Phone number is required';
    else e.phone = phoneError(form.phone) || '';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email address';
    // Drop the empty strings so callers can just count the keys.
    return Object.fromEntries(Object.entries(e).filter(([, v]) => v));
  };

  const submit = async () => {
    const errs = validate();
    setErrors(errs);
    setSendError('');
    if (Object.keys(errs).length) return;
    // The route the customer was looking at when they asked. Without it the ops
    // team gets a name and a pax count and no idea what to quote.
    const flightDetails = buildFlightDetails(useBooking.getState(), payload);
    setSending(true);
    try {
      const res = await requestFlightQuote({
        name: form.name.trim(),
        mobile: form.phone.trim(),
        email: form.email.trim(),
        flightDetails,
        // NUMBERS, not the form's strings. The template prints a count when it is
        // truthy — and the string '0' is truthy, so sending the raw state would
        // put "0 Children, 0 Infants" on every request.
        paxInfo: {
          adults: Number(form.adults) || 0,
          children: Number(form.children) || 0,
          infants: Number(form.infants) || 0,
        },
        budget: form.budget.trim(),
        remarks: form.remarks.trim(),
      });
      // A 2xx is NOT proof on its own: the endpoint answers 200 whatever
      // happened, and only a TOTAL loss — the request neither recorded in the
      // admin queue NOR emailed to anyone — means it reached nobody.
      //
      // A recorded request that could not be emailed is still a request we
      // hold, and telling that customer to try again would only duplicate a
      // lead already waiting to be worked. An absent `recorded` is read as
      // FALSE, so an older backend (which persists nothing) keeps the previous
      // behaviour exactly — a failed send is still reported.
      const recorded = res?.recorded === true;
      const emailed = res?.emailSent !== false;
      if (!recorded && !emailed) {
        setSendError(
          'We could not deliver your request just now. Please try again in a moment,'
          + ' or contact us directly and we will pick it up.',
        );
        return;
      }
      setDone(true);
    } catch (err) {
      // Only a delivered request earns the success screen — the old code showed it
      // unconditionally, which is how a lost enquiry looked identical to a sent one.
      setSendError(
        err?.response?.data?.message
        || 'Could not send your request. Please check your connection and try again.',
      );
    } finally {
      setSending(false);
    }
  };

  const errText = (k) => (errors[k]
    ? <p className="text-[11px] font-semibold text-dangerink mt-1">{errors[k]}</p>
    : null);
  const fieldCls = (k) => 'w-full bg-night/70 border rounded-xl px-3.5 py-2.5 text-sm outline-none '
    + (errors[k] ? 'border-danger focus:border-danger' : 'border-line focus:border-neon');

  return (
    <div className="fixed inset-0 z-[92] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-[92svh] overflow-y-auto no-scrollbar rounded-2xl border border-line bg-panel p-6 sm:p-7">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>

        {!done ? (
          <div>
            <h2 className="font-display text-xl font-extrabold">Flight Request</h2>
            <p className="text-muted text-sm mt-1">Our group booking specialist will provide you the quote within 12 to 24 hours.</p>

            <div className="mt-5 rounded-2xl border border-line bg-night/40 p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold mb-1.5">Name <span className="text-dangerink">*</span></label>
                  <input type="text" placeholder="Your Name" value={form.name} onChange={set('name')} className={fieldCls('name')} />
                  {errText('name')}
                </div>
                <div>
                  <label className="block text-[13px] font-semibold mb-1.5">Phone Number <span className="text-dangerink">*</span></label>
                  <div className={'bg-night/70 border rounded-xl px-3.5 py-2.5 '
                    + (errors.phone ? 'border-danger' : 'border-line focus-within:border-neon')}>
                    <PhoneInput
                      value={form.phone}
                      onChange={(v) => {
                        setForm((f) => ({ ...f, phone: v }));
                        setErrors((p) => (p.phone ? { ...p, phone: '' } : p));
                      }}
                      inputClassName="text-sm"
                    />
                  </div>
                  {errText('phone')}
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5">Email <span className="text-dangerink">*</span></label>
                <input type="email" value={form.email} onChange={set('email')} className={fieldCls('email')} />
                {errText('email')}
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-line bg-night/40 p-5 space-y-4">
              <h3 className="font-semibold">Travellers Details</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold leading-tight">No Of Adults</label>
                  <span className="block text-[10px] text-muted mb-1.5">(Above 12 years)</span>
                  <SelectMenu value={form.adults} onChange={setVal('adults')} options={['1', '2', '3', '4', '5', '6', '7', '8', '9']}
                    triggerClassName="bg-night/70 border border-line rounded-xl px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold leading-tight">No Of Children</label>
                  <span className="block text-[10px] text-muted mb-1.5">(2 - 12 years)</span>
                  <SelectMenu value={form.children} onChange={setVal('children')} options={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
                    triggerClassName="bg-night/70 border border-line rounded-xl px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold leading-tight">No Of Infant(s)</label>
                  <span className="block text-[10px] text-muted mb-1.5">(Below 2 years)</span>
                  <SelectMenu value={form.infants} onChange={setVal('infants')} options={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
                    triggerClassName="bg-night/70 border border-line rounded-xl px-3 py-2.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5">Total Budget (Optional)</label>
                <input type="text" placeholder="Enter Total Budget" value={form.budget} onChange={set('budget')} className="w-full bg-night/70 border border-line rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-neon" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5">Add Remarks (Optional)</label>
                <textarea rows={3} placeholder="Add Remarks" value={form.remarks} onChange={set('remarks')} className="w-full bg-night/70 border border-line rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-neon resize-none" />
              </div>
            </div>

            {/* A failed send says so HERE, beside the button that failed, and the
                form is left intact so the customer can simply press it again. */}
            {sendError && (
              <p className="mt-4 text-[13px] font-semibold text-dangerink">{sendError}</p>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={onClose} disabled={sending} className="px-5 py-2.5 rounded-xl border border-line font-semibold hover:bg-ink/5 cursor-pointer transition disabled:opacity-60 disabled:cursor-not-allowed">GO BACK</button>
              <button
                onClick={submit}
                disabled={sending}
                className="px-6 py-2.5 rounded-xl bg-[#1C1C1C] hover:brightness-125 text-white font-bold cursor-pointer transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? 'SENDING…' : 'SUBMIT REQUEST'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <span className="w-14 h-14 rounded-full bg-neon/15 text-neon flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" className="w-8 h-8"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
            </span>
            <h2 className="font-display text-xl font-extrabold">Request submitted</h2>
            <p className="text-muted text-sm mt-2 max-w-sm mx-auto">Thanks! Our group booking specialist will share your quote within 12 to 24 hours.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 rounded-xl bg-[#1C1C1C] text-white font-bold cursor-pointer hover:brightness-125 transition">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
